import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { User, Calendar, Tag, MessageSquare, Send, CheckCircle, Circle, AlertCircle, Phone, Mail, Archive, Paperclip, XCircle, Clock } from 'lucide-react';
import api from '../../api/api';
import { AuthContext } from '../../context/AuthContext';

const TicketDetail = ({ ticket, onUpdateTicket }) => {
  const { refreshTokenTimestamp } = useContext(AuthContext);
  const [status, setStatus] = useState(ticket.status);
  const [priority, setPriority] = useState(ticket.priority);
  const [newComment, setNewComment] = useState('');
  const [assignedTo, setAssignedTo] = useState(ticket.assignedTo || '');
  const [isSaving, setIsSaving] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [fileAttachment, setFileAttachment] = useState(null);
  
  // Fetch admin users for assignment dropdown
  useEffect(() => {
    // In a real app, this would fetch from API
    // For now, we'll use mock data
    setAdminUsers([
      { id: 'admin-001', name: 'John Doe' },
      { id: 'admin-002', name: 'Jane Smith' },
      { id: 'admin-003', name: 'Alex Johnson' }
    ]);
  }, []);
  
  // Reset state when ticket changes
  useEffect(() => {
    setStatus(ticket.status);
    setPriority(ticket.priority);
    setAssignedTo(ticket.assignedTo || '');
    setNewComment('');
    setFileAttachment(null);
  }, [ticket.id]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle status change
  const handleStatusChange = (e) => {
    setStatus(e.target.value);
  };

  // Handle priority change
  const handlePriorityChange = (e) => {
    setPriority(e.target.value);
  };

  // Handle assignment change
  const handleAssignmentChange = (e) => {
    setAssignedTo(e.target.value);
  };

  // Handle comment submission
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setIsSaving(true);

    try {
      // Send comment to API using api instance
      const response = await api.post(`admin/tickets/${ticket.id}/comments`, {
        text: newComment,
        authorId: 'admin-001'
      });

      const result = response.data;
      
      if (result.success) {
        // Track activity timestamp
        sessionStorage.setItem('lastUserActivity', Date.now().toString());
        
        // Refresh token timestamp if available
        if (refreshTokenTimestamp) {
          refreshTokenTimestamp();
        }
        
        // Update ticket with new comment
        const updatedTicket = result.data.ticket || {
          ...ticket,
          comments: [
            ...(ticket.comments || []),
            result.data.comment
          ]
        };
        
        onUpdateTicket(updatedTicket);
        setNewComment('');
        setFileAttachment(null);
        alert('Reply sent successfully! Employer will be notified.');
      } else {
        console.warn('Failed to add comment:', result.error);
        alert('Failed to send reply. Please try again.');
      }
    } catch (error) {
      console.warn('Could not add comment:', error.message);
      // Don't alert if API endpoint doesn't exist yet
      if (!error.message.includes('404')) {
        alert('Error sending reply. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle file attachment
  const handleFileAttachment = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFileAttachment(e.target.files[0]);
    }
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    setIsSaving(true);
    
    try {
      // Send update to API using api instance
      const response = await api.put(`admin/tickets/${ticket.id}`, {
        status,
        priority,
        assignedTo
      });

      const result = response.data;
      
      if (result.success) {
        // Update local state
        const updatedTicket = {
          ...ticket,
          ...result.data,
          status,
          priority,
          assignedTo: assignedTo || null,
          updatedAt: new Date().toISOString()
        };
        
        onUpdateTicket(updatedTicket);
        alert('Ticket updated successfully!');
      } else {
        console.warn('Failed to update ticket:', result.error);
        alert('Failed to update ticket. Please try again.');
      }
    } catch (error) {
      console.warn('Could not update ticket:', error.message);
      // Don't alert if API endpoint doesn't exist yet
      if (!error.message.includes('404')) {
        alert('Error updating ticket. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    switch (status) {
      case 'open':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle size={12} className="mr-1" />
            Open
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock size={12} className="mr-1" />
            In Progress
          </span>
        );
      case 'resolved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle size={12} className="mr-1" />
            Resolved
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Archive size={12} className="mr-1" />
            Closed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Ticket header */}
      <div className="border-b pb-4 mb-4">
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-bold text-gray-900">{ticket.title}</h2>
          <StatusBadge status={ticket.status} />
        </div>
        <p className="text-sm text-gray-600 mt-2">{ticket.description}</p>
      </div>

      {/* Ticket metadata */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 p-3 rounded-md">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Ticket Information</h3>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <Tag size={14} className="text-gray-400 mr-2" />
              <span className="text-gray-500 mr-1">ID:</span>
              <span className="font-medium">{ticket.id}</span>
            </div>
            <div className="flex items-center text-sm">
              <Calendar size={14} className="text-gray-400 mr-2" />
              <span className="text-gray-500 mr-1">Created:</span>
              <span className="font-medium">{formatDate(ticket.createdAt)}</span>
            </div>
            <div className="flex items-center text-sm">
              <Calendar size={14} className="text-gray-400 mr-2" />
              <span className="text-gray-500 mr-1">Updated:</span>
              <span className="font-medium">{formatDate(ticket.updatedAt)}</span>
            </div>
            <div className="flex items-center text-sm">
              <Tag size={14} className="text-gray-400 mr-2" />
              <span className="text-gray-500 mr-1">Category:</span>
              <span className="font-medium capitalize">{ticket.category}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-3 rounded-md">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Employer Information</h3>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <User size={14} className="text-gray-400 mr-2" />
              <span className="text-gray-500 mr-1">Name:</span>
              <span className="font-medium">{ticket.employer.name}</span>
            </div>
            <div className="flex items-center text-sm">
              <Mail size={14} className="text-gray-400 mr-2" />
              <span className="text-gray-500 mr-1">Email:</span>
              <a href={`mailto:${ticket.employer.email}`} className="font-medium text-blue-600 hover:text-blue-800">
                {ticket.employer.email}
              </a>
            </div>
            <div className="flex items-center text-sm">
              <Phone size={14} className="text-gray-400 mr-2" />
              <span className="text-gray-500 mr-1">Phone:</span>
              <a href={`tel:${ticket.employer.phone}`} className="font-medium text-blue-600 hover:text-blue-800">
                {ticket.employer.phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket management */}
      <div className="bg-gray-50 p-4 rounded-md mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Ticket Management</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={status}
              onChange={handleStatusChange}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div>
            <label htmlFor="priority" className="block text-xs font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              id="priority"
              value={priority}
              onChange={handlePriorityChange}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label htmlFor="assigned" className="block text-xs font-medium text-gray-700 mb-1">
              Assigned To
            </label>
            <select
              id="assigned"
              value={assignedTo}
              onChange={handleAssignmentChange}
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Unassigned</option>
              {adminUsers.map(admin => (
                <option key={admin.id} value={admin.id}>{admin.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>

      {/* Comments section */}
      <div className="flex-1 overflow-y-auto">
        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
          <MessageSquare size={16} className="mr-2" />
          Comments ({ticket.comments.length})
        </h3>
        
        <div className="space-y-4 mb-4 max-h-64 overflow-y-auto">
          {ticket.comments.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No comments yet</p>
          ) : (
            ticket.comments.map((comment) => (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`p-3 rounded-lg ${
                  comment.author === 'admin' ? 'bg-blue-50 ml-4' : 'bg-gray-50 mr-4'
                }`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-700">
                    {comment.author === 'admin' ? 'Support Agent' : 'Employer'}
                    {comment.authorId && ` (${adminUsers.find(a => a.id === comment.authorId)?.name || comment.authorId})`}
                  </span>
                  <span className="text-xs text-gray-500">{formatDate(comment.createdAt)}</span>
                </div>
                <p className="text-sm text-gray-800">{comment.text}</p>
                
                {/* Display attachment if any */}
                {comment.attachment && (
                  <div className="mt-2 p-2 bg-gray-100 rounded-md">
                    <a 
                      href={comment.attachment.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                    >
                      <Paperclip size={12} className="mr-1" />
                      {comment.attachment.name}
                    </a>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>

        {/* Add comment form */}
        <form onSubmit={handleSubmitComment} className="mt-4">
          <div className="flex flex-col space-y-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              rows="3"
              className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            
            <div className="flex items-center justify-between">
              <div className="relative">
                <input
                  type="file"
                  id="file-attachment"
                  onChange={handleFileAttachment}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <label htmlFor="file-attachment" className="flex items-center text-sm text-gray-600 hover:text-gray-800 cursor-pointer">
                  <Paperclip size={16} className="mr-1" />
                  {fileAttachment ? fileAttachment.name : 'Attach file'}
                </label>
              </div>
              
              <button
                type="submit"
                disabled={!newComment.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={16} className="mr-1" />
                Send
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TicketDetail;
